/**
 * Tiny localStorage utility for the "guest save" pending-save mechanism.
 *
 * When a signed-out visitor presses ♡ Save on a quote card, we store the
 * quote ID here before opening the auth modal. After the user signs in or
 * signs up, PendingSaveProcessor picks this up and fires the favorite
 * mutation automatically.
 *
 * - Survives OAuth/page redirects (localStorage persists).
 * - Auto-expires after EXPIRY_MS (30 minutes) to avoid stale saves.
 * - All operations are wrapped in try/catch; localStorage may be
 *   unavailable in private-browsing or when storage is full.
 */

const PENDING_SAVE_KEY = "qda_pendingSave";
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

export interface PendingSave {
  quoteId: number;
  timestamp: number;
}

export function storePendingSave(quoteId: number): void {
  try {
    localStorage.setItem(
      PENDING_SAVE_KEY,
      JSON.stringify({ quoteId, timestamp: Date.now() }),
    );
  } catch {
    // localStorage unavailable — silent no-op; the save prompt still opens
  }
}

export function getPendingSave(): PendingSave | null {
  try {
    const raw = localStorage.getItem(PENDING_SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSave;
    if (
      typeof parsed.quoteId !== "number" ||
      typeof parsed.timestamp !== "number"
    ) {
      clearPendingSave();
      return null;
    }
    if (Date.now() - parsed.timestamp > EXPIRY_MS) {
      clearPendingSave();
      return null;
    }
    return parsed;
  } catch {
    clearPendingSave();
    return null;
  }
}

export function clearPendingSave(): void {
  try {
    localStorage.removeItem(PENDING_SAVE_KEY);
  } catch {
    // ignore
  }
}
